import React, { useState } from 'react';
import { parseISO, formatDistanceToNow, differenceInHours } from 'date-fns';
import { ProcessedStringFact } from '../../hooks/useUserFactsDashboard';
import { cn } from '../../utils/cn';
import { Clock, FileText, Edit2, Trash2, Check, X } from 'lucide-react';

const formatTimeAgo = (dateString: string): string => {
  const date = parseISO(dateString);
  const now = new Date();
  const hours = differenceInHours(now, date);
  
  // Less than 24 hours
  if (hours < 24) {
    return 'Just now';
  }
  
  // Use the same format as numeric cards
  return formatDistanceToNow(date, { addSuffix: true });
};

interface StringFactItemProps {
  fact: ProcessedStringFact;
  className?: string;
  onUpdate?: (key: string, value: string) => Promise<void>;
  onDelete?: (key: string) => Promise<void>;
}

export const StringFactItem: React.FC<StringFactItemProps> = ({
  fact,
  className,
  onUpdate,
  onDelete
}) => {
  const relativeTime = formatTimeAgo(fact.lastUpdated);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(fact.currentValue);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!onUpdate || editValue.trim() === fact.currentValue) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onUpdate(fact.key, editValue.trim());
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update fact:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(fact.currentValue);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    
    if (window.confirm(`Delete "${fact.displayName}"?`)) {
      try {
        await onDelete(fact.key);
      } catch (error) {
        console.error('Failed to delete fact:', error);
      }
    }
  };

  return (
    <div className={cn(
      'bg-white border border-rm-neutral-600/20 rounded-card overflow-hidden shadow-soft hover:shadow-card transition-shadow group',
      className
    )}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h4 className="text-xs font-medium text-rm-neutral-600 uppercase tracking-wide">
              {fact.displayName}
            </h4>
            
            {isEditing ? (
              <div className="mt-1">
                <textarea
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="w-full text-sm text-rm-ink-900 border border-rm-accent-500 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-rm-accent-500 resize-none"
                  rows={3}
                  autoFocus
                  disabled={isSaving}
                />
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={handleSave}
                    disabled={isSaving || !editValue.trim()}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-rm-accent-600 hover:bg-rm-accent-700 rounded transition-colors disabled:opacity-50"
                  >
                    <Check className="w-3 h-3" />
                    Save
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-rm-neutral-600 hover:text-rm-ink-900 border border-rm-neutral-600/30 rounded transition-colors"
                  >
                    <X className="w-3 h-3" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="mt-1 text-sm text-rm-ink-900 break-words">
                {fact.currentValue}
              </p>
            )}
          </div>
          
          {!isEditing && (
            <div className="flex flex-col items-end shrink-0 gap-2">
              <div className="flex items-center gap-1 text-xs text-rm-neutral-600">
                <Clock className="w-3 h-3" />
                <span>{relativeTime}</span>
              </div>
              <span className="text-xs text-rm-neutral-600">
                v{fact.version}
              </span>
              
              {/* Action buttons - show on hover */}
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {onUpdate && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="p-1 text-rm-neutral-600 hover:text-rm-accent-600 hover:bg-rm-bg-50 rounded transition-colors"
                    title="Edit"
                  >
                    <Edit2 className="w-3 h-3" />
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={handleDelete}
                    className="p-1 text-rm-neutral-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface StringFactsListProps {
  facts: ProcessedStringFact[];
  className?: string;
}

export const StringFactsList: React.FC<StringFactsListProps> = ({
  facts,
  className
}) => {
  if (facts.length === 0) {
    return (
      <div className={cn(
        'bg-white border border-rm-neutral-600/20 rounded-card p-6 text-center shadow-soft',
        className
      )}>
        <FileText className="w-8 h-8 mx-auto text-rm-neutral-600 mb-2" />
        <p className="text-sm text-rm-neutral-600">No text-based facts found</p>
        <p className="text-xs text-rm-neutral-600 mt-1">
          Text facts will appear here when collected
        </p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {facts.map((fact) => (
          <StringFactItem key={fact.key} fact={fact} />
        ))}
      </div>
    </div>
  );
};

// Compact summary card for dashboard overview
interface StringFactsSummaryProps {
  facts: ProcessedStringFact[];
  className?: string;
  onViewAll?: () => void;
}

export const StringFactsSummary: React.FC<StringFactsSummaryProps> = ({
  facts,
  className,
  onViewAll
}) => {
  const recentFacts = facts.slice(0, 3);
  
  return (
    <div className={cn(
      'bg-white border border-rm-neutral-600/20 rounded-card p-4 shadow-soft',
      className
    )}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-rm-primary-700" />
          <h3 className="text-sm font-semibold text-rm-primary-700">
            Text Facts
          </h3>
        </div>
        <span className="text-xs text-rm-neutral-600 bg-rm-bg-50 px-2 py-0.5 rounded-full">
          {facts.length} total
        </span>
      </div>
      
      {recentFacts.length > 0 ? (
        <div className="space-y-2">
          {recentFacts.map((fact) => (
            <div key={fact.key} className="py-2 border-b border-rm-neutral-600/10 last:border-0 last:pb-0">
              <div className="text-xs text-rm-neutral-600 uppercase tracking-wide">
                {fact.displayName}
              </div>
              <div className="text-sm text-rm-ink-900 mt-0.5 truncate">
                {fact.currentValue}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-rm-neutral-600 py-2">
          No text facts yet
        </p>
      )}
      
      {facts.length > 3 && onViewAll && (
        <button
          onClick={onViewAll}
          className="mt-3 w-full text-center text-sm text-rm-accent-600 hover:text-rm-accent-700 transition-colors"
        >
          View all {facts.length} facts →
        </button>
      )}
    </div>
  );
};

export default StringFactsList;
